<template>
    <div class="font-semibold mb-4">
        {{ $t('customer.editNewsletter.title') }}
    </div>
    <div class="form-control">
        <label class="relative flex gap-4 items-center cursor-pointer">
            <input 
                v-model="newsletter" 
                type="checkbox" 
                class="toggle toggle-primary"
                @change="updateNewsletterStatus"
            >
            <span class="label-text text-base font-light leading-6">{{ $t('customer.editNewsletter.text') }}</span>
        </label>
    </div>
    <div v-if="loading" class="inline-flex mt-2 py-2 px-3 border rounded border-base-700 bg-base-300">
        <span class="loading mr-4" />
        {{ $t('customer.editNewsletter.stateButtonLoading') }}
    </div>
</template>

<script setup lang="ts">
import { ApiClientError } from '@shopware/api-client'

const { t } = useI18n()
const { user } = useUser()
const { 
    isNewsletterSubscriber, 
    newsletterUnsubscribe, 
    newsletterSubscribe, 
    getNewsletterStatus,
    confirmationNeeded 
} = useNewsletter()
const { showNotification } = useNotification()
const loading = ref(false)
const newsletter = ref(false)
const isUpdatingNewsletter = ref(false)
const localePath = useLocalePath()
const $config = useRuntimeConfig()
const basePath = ($config?.app?.baseURL || '/') === '/' ? '' : ($config?.app?.baseURL || '')

// Hardcoded salutation → Brevo gender mapping (prevents extra salutation API call)
// male → 1, female → 2, all others → 1
const SALUTATION_FEMALE_ID = 'dec4113841954692ab3373dfad3a46fb'

const brevoGender = computed(() =>
    user.value?.salutationId === SALUTATION_FEMALE_ID ? 2 : 1
)

onMounted(async () => {
    try {
        await getNewsletterStatus()
        newsletter.value = isNewsletterSubscriber.value
    } catch (e: unknown) {
        if (e instanceof ApiClientError && e.status === 403) {
            window.location.href = `${basePath}${localePath('/customer/login')}?returnUrl=/customer/account`
            return
        }
    }
})

const updateNewsletterStatus = async () => {
    if (isUpdatingNewsletter.value) return
    
    isUpdatingNewsletter.value = true
    try {
        if (newsletter.value) {
            await newsletterSubscribe({
                email: user.value?.email || '',
                salutationId: user.value?.salutationId,
                option: 'subscribe',
                customFields: {
                    a4g_brevo_newsletter_gender: brevoGender.value
                }
            })
        } else {
            await newsletterUnsubscribe(user.value?.email || '')
        }
    } catch (error) {
        console.error('Newsletter update error:', error)
        showNotification(t('customer.editNewsletter.error'), 'error', true)
    } finally {
        await getNewsletterStatus()
        newsletter.value = isNewsletterSubscriber.value
        isUpdatingNewsletter.value = false
        
        if (isNewsletterSubscriber.value && !confirmationNeeded.value) {
            showNotification(t('customer.editNewsletter["subscribe.success"]'), 'success')
        }

        if (!isNewsletterSubscriber.value) {
            showNotification(t('customer.editNewsletter["unsubscribe.success"]'), 'success')
        }

        if (confirmationNeeded.value) {
            showNotification(t('customer.editNewsletter["subscribe.success"]'), 'success')
        }
    }
}
</script>
